import React, { useState, useEffect, Fragment } from "react";
import { Table, Input, notification, Spin, Select, Button, Tooltip } from "antd";
import { Link } from "react-router-dom";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import { DownloadOutlined, EyeOutlined, SearchOutlined } from "@ant-design/icons";
import Breadcrumb from "../common/breadcrumb";
import { callGetApiToServer } from "../../Graphs/callApi";
import { WEBURL } from "../../constant/comman";
import moment from "moment";

const { Option } = Select;

const NativeSelect = ({ value, onChange, children, className }) => {
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className={className}
      style={{
        padding: "3px 8px",
        borderRadius: "4px",
        border: "1px solid #d9d9d9",
        backgroundColor: "#fff",
        outline: "none",
        cursor: "pointer",
        marginLeft: "8px",
        height: "28px"
      }}
    >
      {React.Children.map(children, (child) => {
        if (!child) return null;
        return <option value={child.props.value}>{child.props.children}</option>;
      })}
    </select>
  );
};

const AssignmentSubmissionsList = (props) => {
  const { authToken, match } = props;
  const questionId = match.params.questionId;

  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "Submitted", "Pending"

  const fetchSubmissions = async () => {
    setIsLoading(true);
    try {
      const header = {
        Accept: "application/json",
        "Content-Type": "application/json",
        authorization: "Bearer " + authToken,
      };

      const skip = (currentPage - 1) * pageSize;
      const endUrl = `v1/auth/admin/assignment-submissions/questions/${questionId}/submissions?skip=${skip}&limit=${pageSize}&search=${encodeURIComponent(searchText)}&statusFilter=${statusFilter}`;
      const res = await callGetApiToServer(header, "GET", endUrl);

      if (res.status === 200) {
        setSubmissions(res.data || []);
        setTotal(res.total || 0);
      } else {
        notification.error({
          message: "Error",
          description: res.message || "Failed to fetch submissions",
        });
      }
    } catch (error) {
      console.error("Error fetching submissions:", error);
      notification.error({
        message: "Error",
        description: "Something went wrong while fetching student submissions.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, [authToken, questionId, currentPage, pageSize, searchText, statusFilter]);

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const getFileUrl = (fileName) => {
    if (!fileName) return "";
    if (fileName.startsWith("http://") || fileName.startsWith("https://")) {
      return fileName;
    }
    // Prepend host URL and upload directory serving path
    const baseUrl = WEBURL.endsWith("/") ? WEBURL : WEBURL + "/";
    return `${baseUrl}course/topic/question/answer/${fileName}`;
  };

  const columns = [
    {
      title: "Student Name",
      dataIndex: "student_name",
      key: "student_name",
      render: (text, record) => (
        <Link to={`/assignment-submissions/submissions/${record._id}/question/${questionId}`} style={{ fontWeight: "600", color: "#4466f2" }}>
          {text}
        </Link>
      ),
    },
    {
      title: "Student Email",
      dataIndex: "student_email",
      key: "student_email",
    },
    {
      title: "Student ID",
      dataIndex: "student_id",
      key: "student_id",
      render: (id) => id || "N/A",
    },
    {
      title: "Submission Date & Time",
      dataIndex: "submission_date",
      key: "submission_date",
      render: (date) => (date ? moment(date).format("DD-MM-YYYY HH:mm") : "N/A"),
    },
    {
      title: "Status",
      dataIndex: "submission_status",
      key: "submission_status",
      render: (status) => {
        const color = status === "Submitted" ? "badge badge-success" : "badge badge-warning";
        return <span className={color}>{status || "N/A"}</span>;
      },
    },
    {
      title: "Answer Document",
      dataIndex: "uploaded_document",
      key: "uploaded_document",
      align: "center",
      render: (fileName) => {
        if (!fileName) return "No document uploaded";
        const fileUrl = getFileUrl(fileName);
        return (
          <div className="d-flex justify-content-center" style={{ gap: "8px" }} onClick={(e) => e.stopPropagation()}>
            <a href={fileUrl} target="_blank" rel="noopener noreferrer">
              <Button type="primary" size="small" icon={<EyeOutlined />}>View</Button>
            </a>
            <a href={fileUrl} download target="_blank" rel="noopener noreferrer">
              <Button type="default" size="small" icon={<DownloadOutlined />}>Download</Button>
            </a>
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Tooltip title="Review Submission">
          <Button
            type="text"
            shape="circle"
            icon={<EyeOutlined style={{ color: "#5c6ac4" }} />}
            onClick={(e) => {
              e.stopPropagation();
              props.history.push(`/assignment-submissions/submissions/${record._id}/question/${questionId}`);
            }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Fragment>
      <Breadcrumb title="Student Submissions" parent="Assignment Submissions" child="Submissions" />
      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap" style={{ gap: "10px" }}>
            <h5>Student Submission Records</h5>
            <div className="d-flex align-items-center flex-wrap" style={{ gap: "15px" }}>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  padding: "5px 10px",
                  borderRadius: "4px",
                  border: "1px solid #d9d9d9",
                  backgroundColor: "#fff",
                  outline: "none",
                  cursor: "pointer",
                  height: "38px",
                  minWidth: "180px"
                }}
              >
                <option value="all">All Submissions</option>
                <option value="Submitted">Submitted</option>
                <option value="Pending">Pending</option>
              </select>
              <div style={{ position: "relative", width: "250px", height: "38px" }}>
                <span style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#bfbfbf",
                  display: "flex",
                  alignItems: "center"
                }}>
                  <SearchOutlined />
                </span>
                <input
                  type="text"
                  placeholder="Search students..."
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{
                    width: "100%",
                    height: "38px",
                    padding: "0 30px 0 35px",
                    borderRadius: "4px",
                    border: "1px solid #d9d9d9",
                    outline: "none",
                    fontSize: "14px",
                    boxSizing: "border-box"
                  }}
                />
                {searchText && (
                  <span
                    onClick={() => handleSearch("")}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#bfbfbf",
                      cursor: "pointer",
                      fontSize: "16px",
                      display: "flex",
                      alignItems: "center",
                      userSelect: "none"
                    }}
                  >
                    &times;
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="card-body">
            {isLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={submissions}
                rowKey="_id"
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: total,
                  showSizeChanger: true,
                  selectComponentClass: NativeSelect,
                }}
                onChange={(pagination) => {
                  setCurrentPage(pagination.current);
                  setPageSize(pagination.pageSize);
                }}
                onRow={(record) => ({
                  onClick: () => {
                    props.history.push(`/assignment-submissions/submissions/${record._id}/question/${questionId}`);
                  },
                  style: { cursor: "pointer" }
                })}
                locale={{ emptyText: "No student submissions found." }}
              />
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  authToken: state.auth.authToken,
});

export default connect(mapStateToProps)(withRouter(AssignmentSubmissionsList));
